import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spacing from '../componenets/UI/Spacing'
import axiosClient from '../config/axios-client'
import FutureMineralCard from '../componenets/global/FutureMineralCard'
import { useLocation, useParams } from 'react-router'
import Pagination from '../componenets/UI/Pagination'
import { useSearchParams } from 'react-router-dom'
import FutureMineralsGridLoading from '../componenets/future-minerals/FutureMineralsGridLoading'
import { Helmet } from 'react-helmet'

export default function FutureMineralsPage() {
  const { t } = useTranslation()
  const { lng } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const searchQueryRef = useRef(null)
  const [minerals, setMinerals] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState(null)

  const [selectedMineralCategoryId, setSelectedMineralCategoryId] = useState(searchParams.get('category'))

  const handleOnChangeMineralCategoryId = useCallback(
    (id) => {
      searchParams.set('page', 1)
      searchQueryRef.current.value = ''
      searchParams.delete('search_query')
      if (id == selectedMineralCategoryId) {
        // Reset the category if the same id is clicked
        searchParams.delete('category')
        setSelectedMineralCategoryId(0)
      } else {
        searchParams.set('category', id)
        setSelectedMineralCategoryId(id)
      }
      setSearchParams(searchParams)
    },
    [searchParams, selectedMineralCategoryId, setSearchParams],
  )

  const getFutureMinerals = useCallback(() => {
    window.scrollTo(0, 0)
    setIsLoading(true)
    axiosClient
      .get(`/platform/minerals?${searchParams.toString()}`)
      .then(({ data }) => {
        setMinerals(data.minerals)
        setPagination({
          links: data.links,
          current_page: data.current_page,
          first_page: data.first_page,
          last_page: data.last_page,
        })
      })
      .catch((error) => { })
      .finally(() => {
        setIsLoading(false)
      })
  }, [searchParams])

  useEffect(() => {
    getFutureMinerals()
  }, [getFutureMinerals])

  const handleOnSumitSearch = (event) => {
    event.preventDefault()
    if (searchQueryRef.current.value.length !== 0) {
      searchParams.set('search_query', searchQueryRef.current.value)
      setSearchParams(searchParams)
    }
  }
  const handleOnChangeSearchQueryInput = (event) => {
    const inputValue = event.target.value
    if (inputValue.length === 0) {
      searchParams.set('page', 1)
      searchParams.delete('search_query')
      setSearchParams(searchParams)
    }
  }

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const categoryId = query.get('category')
    setSelectedMineralCategoryId(categoryId || '')
  }, [location])

  useEffect(() => {
    if (searchParams.get('search_query')) {
      if (searchQueryRef.current && !searchQueryRef.current.value) {
        searchQueryRef.current.value = searchParams.get('search_query')
      }
    }
  }, [])
  return (
    <>
      <Helmet>
        <title>{`APFM | ${t('FUTURE_MINERALS')}`}</title>
      </Helmet>
      <Spacing />
      <div className="app-container px-8 lg:px-0">
        <div
          data-aos={`fade-down`}
          data-aos-delay={100}
          className="mb-4"
        >
          <h2 className="text-2xl font-black">{t('FUTURE_MINERALS')}</h2>
        </div>
        <div
          data-aos={`fade-up`}
          data-aos-delay={100}
          className="mb-4 flex gap-2"
        >
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOnChangeMineralCategoryId(1)}
              title={t('CLEAN_ENERGY_MINERALS')}
              className={`flex items-center border-2 p-2 outline-none ${selectedMineralCategoryId == 1 ? 'bg-muted-300 border-cleanEnergy' : 'border-muted-300 hover:bg-muted-300 bg-white hover:border-cleanEnergy'}`}
            >
              <img
                src="/assets/icons/clean-energy-color/clean-energy-icon.svg"
                alt=""
                className="h-5 min-h-5 w-5 min-w-5"
              />
            </button>
            <button
              onClick={() => handleOnChangeMineralCategoryId(2)}
              title={t('INDUSTRIAL_MINERALS')}
              className={`flex items-center border-2 p-2 outline-none ${selectedMineralCategoryId == 2 ? 'bg-muted-300 border-industrial' : 'border-muted-300 hover:bg-muted-300 bg-white hover:border-industrial'}`}
            >
              <img
                src="/assets/icons/industrial-color/industrial-icon.svg"
                alt=""
                className="h-5 min-h-5 w-5 min-w-5"
              />
            </button>
            <button
              onClick={() => handleOnChangeMineralCategoryId(4)}
              title={t('FOOD_SECURITY_MINERALS')}
              className={`flex items-center border-2 p-2 outline-none ${selectedMineralCategoryId == 4 ? 'bg-muted-300 border-security' : 'border-muted-300 hover:bg-muted-300 bg-white hover:border-security'}`}
            >
              <img
                src="/assets/icons/security-color-green/security-food-icon.svg"
                alt=""
                className="h-5 min-h-5 w-5 min-w-5"
              />
            </button>
          </div>
          <form
            className="flex w-full gap-2"
            onSubmit={handleOnSumitSearch}
          >
            <input
              onChange={handleOnChangeSearchQueryInput}
              type="search"
              ref={searchQueryRef}
              className="text-primary-600 border-muted-300 w-full border-2 px-4 py-1.5 outline-none"
            />
            <button
              type="submit"
              className="border-primary-600 bg-primary-600 hover:border-secondary-600 hover:text-secondary-600 hover:bg-muted-300 border-2 px-6 text-white"
            >
              {t('SEARCH')}
            </button>
          </form>
        </div>

        <div
          data-aos={`fade-up`}
          data-aos-delay={200}
          className="mb-4 grid grid-cols-12 gap-4"
        >
          {isLoading ? (
            <FutureMineralsGridLoading />
          ) : (
            minerals?.map((mineral) => (
              <FutureMineralCard
                isGridChild={true}
                mineral={mineral}
                link={`/${lng}/future-minerals/${mineral.slug}`}
                key={mineral.id}
                lng={lng}
              />
            ))
          )}
        </div>
        {!isLoading && <Pagination pagination={pagination} />}
      </div>
    </>
  )
}
